import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';

const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ account_code: '', account_name: '', account_type: 'Expense', parent_account_id: '' });

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await accountsApi.get('/chart-of-accounts');
      setAccounts(response.data.accounts);
    } catch (err) {
      toast.error('Failed to load chart of accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const filtered = typeFilter ? accounts.filter(a => a.account_type === typeFilter) : accounts;

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await accountsApi.post('/chart-of-accounts', {
        account_code: form.account_code,
        account_name: form.account_name,
        account_type: form.account_type,
        parent_account_id: form.parent_account_id || null,
      });
      toast.success('Account created');
      setShowCreateModal(false);
      setForm({ account_code: '', account_name: '', account_type: 'Expense', parent_account_id: '' });
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create account');
    }
  };

  const toggleActive = async (account) => {
    try {
      await accountsApi.put(`/chart-of-accounts/${account.id}`, { is_active: !account.is_active });
      toast.success(account.is_active ? 'Account deactivated' : 'Account activated');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to update account');
    }
  };

  return (
    <Layout title="Chart of Accounts">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-3">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900"
        >
          <Plus size={16} /> New Account
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Classification</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Normal Balance</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No accounts found</td></tr>
            ) : filtered.map(a => (
              <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{a.account_code}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{a.account_name}</td>
                <td className="px-4 py-3 text-gray-600">{a.account_type}</td>
                <td className="px-4 py-3 text-gray-600">{a.classification || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{a.normal_balance || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {a.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleActive(a)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                    {a.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-sm text-gray-500 mt-2">Total: {filtered.length} accounts</div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><BookOpen size={18} /> New Account</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Account Code</label>
                <input required type="text" value={form.account_code}
                  onChange={(e) => setForm({ ...form, account_code: e.target.value })}
                  placeholder="e.g. 5910"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Account Name</label>
                <input required type="text" value={form.account_name}
                  onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Account Type</label>
                <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Parent Account (optional)</label>
                <select value={form.parent_account_id} onChange={(e) => setForm({ ...form, parent_account_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">None (top-level)</option>
                  {accounts.filter(a => !a.parent_account_id).map(a => (
                    <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-400">Classification and normal balance can be set later via the database seed for standard accounts; ad-hoc accounts created here start unclassified.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ChartOfAccounts;
